/*
 * wf-bogo-auto-discount.js
 * ------------------------------------------------------------------------
 * Auto-applied BUT removable "Buy 1 Get 1 50% off" sale.
 *
 * WHY THIS EXISTS
 *   The BOGO used to be a native *automatic* Buy X Get Y discount. Automatic
 *   discounts can't be removed by the shopper and don't combine with codes, so
 *   a customer's own code (e.g. WELCOME-20, or a Klaviyo /discount/<code>
 *   email link) was rejected at checkout:
 *       "WELCOME-20 couldn't be used with your existing discounts."
 *
 *   A Shopify discount Function can't fix this on our plan: the discount
 *   .run target can't see the buyer's entered codes, the .fetch target that
 *   can is Plus/Enterprise-only, and the Discounts Allocator is dev-preview.
 *
 *   So instead the BOGO is now a CODE-based Buy X Get Y discount (see
 *   ADMIN SETUP below). This script keeps that code applied by default but
 *   yields to ANY customer code — giving a true "replace", with no error.
 *
 * BEHAVIOUR
 *   - No customer code on the cart            -> ensure the BOGO code is applied.
 *   - An applicable customer code is present  -> ensure the BOGO code is gone,
 *     so only the customer's code applies (no "can't combine" error). Works the
 *     same whether they typed the code or arrived via a /discount/<code> link.
 *
 * ADMIN SETUP (one time)
 *   1. Discounts -> create a *Discount code* Buy X Get Y matching the old
 *      automatic one (Buy 1, get 1 at 50% off, same collections). Set its code
 *      to exactly window.WF_BOGO.code below (see theme.liquid).
 *   2. Turn OFF the old *automatic* "BUY 1 GET 1 50% OFF" discount.
 *
 * MECHANISM
 *   Summer '25 cart/update.js discount support: POST {discount:"A,B"} REPLACES
 *   the cart's whole code set; {discount:""} clears it. cart.discount_codes
 *   returns [{code, applicable}].
 *
 * Debug: append ?bogo-debug=1 to any URL to log decisions to the console.
 * ------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var cfg = window.WF_BOGO || {};
  var BOGO_CODE = String(cfg.code || 'BOGO50').toUpperCase();
  var DEBUG = /[?&]bogo-debug=1/.test(window.location.search) || !!cfg.debug;

  var busy = false; // re-entrancy guard so overlapping cart events don't stampede

  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[wf-bogo]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  // Replace the cart's discount-code set with `codes` (array of original-cased
  // strings). [] clears all codes. Uses fetch on purpose: jQuery's ajaxComplete
  // (our reconcile trigger) does not observe fetch, so this write can't loop.
  function setDiscount(codes) {
    var value = codes.join(',');
    log('write discount ->', JSON.stringify(value));
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: value })
    }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }

  function isBogo(d) { return (d.code || '').toUpperCase() === BOGO_CODE; }
  function keepCustomerCodes(dc) {
    return dc.filter(function (d) { return !isBogo(d); }).map(function (d) { return d.code; });
  }

  // Core reconcile. Goal: the cart carries the BEST single, valid discount — a
  // customer's own code when it works, otherwise the auto-sale — and NEVER both
  // the sale code and a customer code at once (that pairing is what triggers the
  // "couldn't be combined" error at checkout).
  function reconcile(reason) {
    if (busy) { log('skip (busy)', reason); return Promise.resolve(); }
    busy = true;
    return getCart().then(function (cart) {
      if (!cart) { log('no cart'); return; }
      var dc = cart.discount_codes || []; // [{code, applicable}]
      var hasBogo = dc.some(isBogo);
      var nonBogo = dc.filter(function (d) { return !isBogo(d); });
      log('reason=' + reason, 'items=' + cart.item_count, 'codes=' + JSON.stringify(dc));

      // No customer code -> keep the auto-sale on (when the cart has items).
      if (nonBogo.length === 0) {
        if (!hasBogo && cart.item_count > 0) return setDiscount([BOGO_CODE]);
        return;
      }

      // Customer code present AND already applicable -> let it win; just make
      // sure the sale code isn't also on the cart.
      if (nonBogo.some(function (d) { return d.applicable === true; })) {
        if (hasBogo) return setDiscount(keepCustomerCodes(dc));
        return;
      }

      // Customer code present but NOT applicable right now. It's usually just
      // masked by the sale code (the two can't combine — exactly the email-link
      // case). If the sale is on, drop it and re-check: if their code then
      // works, the customer wins; if it still doesn't, their code is genuinely
      // invalid on this cart, so put the sale back so they still get a discount.
      if (!hasBogo) return; // can't safely add the sale alongside their code
      return setDiscount(keepCustomerCodes(dc))
        .then(function () { return getCart(); })
        .then(function (after) {
          var works = ((after && after.discount_codes) || []).some(function (d) {
            return d.applicable === true && !isBogo(d);
          });
          if (!works) {
            log('customer code invalid on this cart -> restoring sale');
            return setDiscount([BOGO_CODE]);
          }
          log('customer code now applies -> sale dropped');
        });
    }).catch(function (e) { log('error', e); }).finally(function () { busy = false; });
  }

  // Light debounce: collapse bursts of cart events into one reconcile.
  var t = null;
  function schedule(reason) {
    if (t) clearTimeout(t);
    t = setTimeout(function () { t = null; reconcile(reason); }, 80);
  }

  // 1) Initial pass — covers normal loads AND landing via a /discount/<code>
  //    email link (the code is already on the cart by the time we read it).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { schedule('load'); });
  } else {
    schedule('load');
  }

  // 2) After any jQuery cart mutation (add / change / update). jQuery's
  //    ajaxComplete fires for the theme's $.ajax cart calls but NOT for our
  //    fetch write above, so this can't feed back into itself. Wait for jQuery
  //    in case vendor.js hasn't finished loading when this runs.
  function bindCartEvents() {
    if (!window.jQuery) return false;
    jQuery(document).ajaxComplete(function (event, xhr, settings) {
      var url = settings && settings.url ? settings.url : '';
      if (/\/cart\/(add|change|update)/.test(url)) schedule('cart-ajax');
    });
    return true;
  }
  if (!bindCartEvents()) {
    var tries = 0;
    var iv = setInterval(function () {
      if (bindCartEvents() || ++tries > 50) clearInterval(iv);
    }, 100);
  }

  // Expose for manual testing / a "remove sale" control if we add one later.
  window.WFBogo = { reconcile: reconcile, code: BOGO_CODE };
})();
